import { defineCopy } from '@floorfile/i18n'

/**
 * Settings copy.
 *
 * Started with the localization page, which was — and this is the part worth
 * recording — the only screen in the app whose entire subject is language and
 * whose own strings were hardcoded English. The rest of the slice migrates in
 * batch 5f: chunk **5f-1** takes the COMPANY half (the hub, company profile,
 * policies, numbering, features, the audit log and system health); chunk
 * **5f-2** takes the users-and-access half.
 *
 * ## What the lint rule could not see here
 *
 * Almost all of it. Six of this half's seven screens keep their words in a
 * MODULE CONSTANT — `CARDS` (26 cards × title + description), `POLICIES`,
 * `FEATURES`, `BANNER`, and an operating-field TUPLE array — which is defect
 * class 1 at a scale no other slice has produced. The measured literal count
 * for these files was 66; the real string count is more than twice that.
 *
 * ## Deliberately NOT translated
 *
 * - **IANA time-zone identifiers** (`America/Toronto`). They are database
 *   keys, they are what an admin matches against a plant address, and
 *   `Intl.supportedValuesOf('timeZone')` returns them in exactly one spelling.
 * - **The company slug** and **custom automation flag names**
 *   (`pilot.new_flow`). The slug is a URL segment; a flag name is read by
 *   workflow conditions and webhook payloads, so translating it would break
 *   the integrations it exists for.
 * - **Cron job ids** on system health (`ai-process-jobs`) and **dependency
 *   names** — both are what an engineer quotes in a support ticket.
 * - **Numbering prefixes** (`CAR`, `NCR`) and the sample numbers built from
 *   them. A prefix is stamped onto records permanently.
 * - **`audit_log.action` and `entityType`** — machine identifiers on the
 *   immutable trail, rendered as code. Rule 8: the next reader is an auditor
 *   comparing them against the API, not a French speaker reading prose.
 */
export const settingsCopy = defineCopy('settings', {
  localization: {
    title: 'Language & localization',
    description: 'Set the company language default and whether people may choose their own language.',
    defaultLanguage: 'Default company language',
    /** F35: the select's one-line description below it. */
    defaultLanguageHelp: 'Sets the language new users and screens without a personal choice start in.',
    allowOverrideLabel: 'Allow personal language selection',
    allowOverrideHint: 'When off, the company default overrides personal preferences.',
    save: 'Save localization',
    saving: 'Saving…',
    saved: 'Saved.',
  },

  hub: {
    /** Owner, 2026-09-22: the hub is a grouped register, so its rows need column headers. */
    column: {
      name: 'Setting',
      description: 'What it does',
    },
    title: 'Settings',
    description: 'Company-wide configuration: profile, users, policies, and capabilities.',
    /**
     * FF-AUDIT-20260921-210633-009: 27 equal cards in one undifferentiated
     * grid, in declaration order, with "My profile" under a heading that said
     * "Company-wide configuration" and two audit LOGS filed as settings. The
     * groups below are what an admin is actually looking for when they open
     * this page.
     */
    group: {
      company: 'Company',
      people: 'People and access',
      operations: 'Operations',
      quality: 'Quality and compliance',
      records: 'Records and logs',
      system: 'System',
    },
    /** Records are not settings; they are filed here because this is where people look. */
    groupNote: { records: 'Read-only history.' },
    searchLabel: 'Search settings',
    searchPlaceholder: 'Search settings…',
    searchCount: { one: '{count} setting matches', other: '{count} settings match' },
    searchNone: 'No setting matches “{query}”.',
    clearSearch: 'Clear',
    /** FF-AUDIT-20260921-210633-009: every sub-page's way back. */
    backToSettings: '← Settings',
    /**
     * The hub's 26 cards, keyed BY ROUTE PATH exactly like the shell's
     * `nav.items` catalogue in `app/copy.ts` — the path is the card's identity
     * and its address at the same time, so keying on it means a card whose
     * route moves fails the typecheck instead of silently losing its words.
     *
     * Was the `CARDS` module constant: 52 strings the copy rule cannot see,
     * because a module-level array is not JSX.
     */
    cardTitle: {
      '/account': 'My profile',
      '/settings/company': 'Company profile',
      '/settings/users': 'Users',
      '/settings/facilities': 'Facilities',
      '/settings/policies': 'Policies',
      '/settings/features': 'Features',
      '/settings/access': 'Access',
      '/settings/assignment-rules': 'Assignment rules',
      '/settings/work-transfer': 'Transfer work',
      '/settings/teams': 'Teams',
      '/settings/localization': 'Language & localization',
      '/settings/numbering': 'Numbering',
      '/settings/checks': 'Checks',
      '/settings/parts': 'Parts & reordering',
      '/settings/equipment-metrics': 'Equipment metrics',
      '/settings/workforce': 'Workforce',
      '/settings/hr-records': 'HR record types',
      '/settings/hr-access-log': 'HR access log',
      '/settings/modules': 'Modules',
      '/settings/notifications': 'Notifications',
      '/settings/search': 'Search',
      '/search/bulk': 'Labels & codes',
      '/settings/governance': 'Data retention and legal holds',
      '/settings/audit-log': 'Audit log',
      '/integrations': 'Integrations',
      '/automation': 'Automation',
      '/settings/change-control': 'Change control',
      '/forms': 'Forms',
      '/settings/deleted-forms': 'Deleted forms',
      '/settings/system-health': 'System health',
    },
    cardDescription: {
      '/account': 'Avatar, name, theme, language, password, 2FA, and sessions.',
      '/settings/company': 'Name and time zone.',
      '/settings/users': 'Invite, roles, permission overrides, sessions.',
      '/settings/facilities': 'Sites this company operates, default visibility per module.',
      '/settings/policies': 'Deletion, archive visibility, mandatory 2FA.',
      '/settings/features': 'Uploads, exports, chat, AI, dashboards, and operating switches.',
      '/settings/access': 'Role-by-page visibility and API access matrix.',
      '/settings/assignment-rules': 'Who may give each kind of work to whom, and per-person exceptions.',
      '/settings/work-transfer': 'Move everything a person who left or is away still holds to someone else — and send it back.',
      '/settings/teams': 'Teams, who leads them, and the equipment each answers for.',
      '/settings/localization': 'Company language default and personal overrides.',
      '/settings/numbering': 'Entity-number prefixes and padding.',
      '/settings/checks': 'Check types, and which form each check loads.',
      '/settings/parts': 'Low-stock behavior, who receives reorder requests, supplier emails.',
      '/settings/equipment-metrics': 'The metric catalogue every asset draws from: units, decimals, plausible ranges.',
      '/settings/workforce': 'Schedule auto-generation, the vacation year, and carryover policy.',
      '/settings/hr-records': 'Record types, their forms, confidentiality and escalation ladder.',
      '/settings/hr-access-log': 'Who viewed which HR record, and when.',
      '/settings/modules': 'Capabilities this company uses.',
      '/settings/notifications': 'Company-wide alert rules: who is told, and when.',
      '/settings/search': 'Reindexing and search configuration.',
      '/search/bulk': 'Resolve bulk identifiers and create QR, barcode, NFC, alias, and asset-tag labels.',
      '/settings/governance': 'Retention, legal holds, erasure requests.',
      '/settings/audit-log': 'Immutable company-wide record of business mutations and corrections.',
      '/integrations': 'Webhooks, API clients, connectors, workflows.',
      '/automation': 'Rules that react to events: notify, assign, escalate.',
      '/settings/change-control': 'Approval matrix for change requests.',
      '/forms': 'The form library — build once, render on checks, audits and records.',
      '/settings/deleted-forms': 'Restore forms removed from the library.',
      '/settings/system-health': 'Service status and background job health.',
    },
  },

  company: {
    title: 'Company profile',
    description: 'Your company name and time zone — every date and due time reads against this zone.',
    name: 'Company name',
    /** F35: shown on the Input primitive as the field's one-line description. */
    nameHelp: 'Shown on invoices, generated documents and throughout the app.',
    logo: 'Company logo',
    logoAlt: 'Company logo',
    noLogo: 'No logo uploaded.',
    replaceLogo: 'Replace logo',
    uploadLogo: 'Upload logo',
    timezone: 'Time zone',
    /** F35: shown when the typed text IS a known zone; `timezoneUnknown` below
     * replaces it (the primitive shows help OR error, never both) when not. */
    timezoneHelp: 'Sets the zone every date and due time is calculated and shown in.',
    /** FF-AUDIT-20260921-210633-016: 420 zones, no search. Type to narrow. */
    timezonePlaceholder: 'Start typing a city — e.g. Toronto',
    timezoneUnknown: 'Pick a time zone from the list.',
    /** The company's URL segment — an identifier, shown so an admin can quote
     * it, never translated. */
    slug: 'Slug: {slug}',
    saveFailed: 'Could not save the company profile.',
    saved: 'Saved.',
    save: 'Save',
    saving: 'Saving…',
    readOnly: 'You have read-only access to this page.',
  },

  policies: {
    title: 'Policies',
    description:
      'Company-wide behavior switches — every automated behavior is something you control here.',
    /** Was the `POLICIES` module constant. */
    label: {
      allowFullDelete: 'Allow permanent (hard) delete',
      showArchivedItems: 'Show archived items by default',
      require2fa: 'Require two-factor authentication for everyone',
      requireOfflineEncryption: 'Require offline encryption',
    },
    consequence: {
      allowFullDelete:
        'When off (the default), delete actions only archive records — nothing is destroyed and everything stays recoverable. When on, users with the right permission can permanently and irreversibly erase a record.',
      showArchivedItems:
        'When on, archived (soft-deleted) records appear in registers and search by default, clearly marked. When off, archived records are hidden unless explicitly filtered in.',
      require2fa:
        'When on, every user without 2FA enrolled is dropped into an enrollment-only session on their next token refresh (within 15 minutes) — they can only complete 2FA setup until they enroll.',
      requireOfflineEncryption:
        'Every user must set an offline PIN; on-device data is always encrypted and users cannot turn it off.',
    },
    /**
     * The switch's screen-reader label. Was `{value ? 'Disable' : 'Enable'}
     * {policy.label}` — a verb concatenated onto a noun phrase, which French
     * cannot reassemble in that order (rule 5).
     */
    switchOn: 'Enable {policy}',
    switchOff: 'Disable {policy}',
    updateFailed: 'Could not update the policy.',

    /** Owner decision 2026-09-16 (round 2): the deviations / OOS separation-of-duties switch. */
    /** F8.2. */
    teamRoutingTitle: 'Work for a machine’s responsible team',
    teamRoutingBody: 'When a machine names a responsible team, new work nobody is named on goes to that team first.',
    teamRoutingAllow: 'Send new work to the machine’s responsible team',
    teamRoutingHelp: 'On: FloorFile picks one of the team’s members who is on shift, or offers the work to the whole team. Off: it chooses from everyone qualified, as before.',
    sodTitle: 'Separation of duties — deviations and OOS',
    sodBody: 'By default the person who raises a deviation cannot approve it, and the person who opens an OOS investigation cannot conclude it — the same rule change control applies.',
    sodAllow: 'Allow the person who raised a deviation or OOS investigation to approve or conclude it',
    sodAllowHelp: 'Only for a one-person quality function. The record will show the same person raising and approving.',
    sodExemptionHelp: 'Regardless of this switch, a person granted “May approve deviations and conclude OOS investigations they raised themselves” in Settings → Users is exempt. Nobody holds that by role, owners and admins included.',
    /** FF-QA-084 (owner decision 2026-09-27): who may approve a deviation, and who may raise a planned one. */
    deviationApprovalTitle: 'Deviation approval — who decides',
    deviationApprovalBody: 'Owners and administrators may approve or reject a deviation and raise a planned one. Name more people below, or let every supervisor in. The people who may approve are the ones told when a deviation is sent to management for approval.',
    supervisorsMayApprove: 'Supervisors may approve and reject deviations',
    supervisorsMayApproveHelp: 'Off: a supervisor who holds the approve permission still cannot decide — only owners, administrators and the named approvers can.',
    approverUsers: 'Named approvers',
    approverUsersHelp: 'These people may approve or reject a deviation whatever their role.',
    supervisorsMaySubmitPlanned: 'Supervisors may raise planned deviations',
    supervisorsMaySubmitPlannedHelp: 'Off: a supervisor records what happened as an unplanned deviation, but cannot plan one in advance.',
    plannedSubmitterUsers: 'Named planned-deviation submitters',
    plannedSubmitterUsersHelp: 'These people may raise a planned deviation whatever their role. Everyone else with the create permission records unplanned deviations only.',
    /** Owner decisions 2026-09-18 (Fiix build): currency + who sees labour cost. */
    costsTitle: 'Costs',
    costsBody: 'How money is shown, and who may see the labour-cost estimate on a work order.',
    currency: 'Currency',
    currencyHelp: 'A three-letter code such as CAD, USD or EUR. Leave blank to show plain numbers.',
    currencyInvalid: 'Enter a three-letter currency code, such as CAD.',
    currencySave: 'Save currency',
    laborCostRoles: 'Show costs to',
    laborCostRolesHelp: 'Every derived cost in the app: a work order’s labour estimate (timed hours × the technician’s job-title wage), each machine’s hourly downtime rate, and a stop’s cost breakdown — labour, parts, production loss and other costs. Wages themselves stay behind the “read sensitive workforce data” permission.',
    /** F3 (OD-6): who sees an asset's and a part's change history. */
    historyAccess: 'Who can see change history',
    historyAccessHelp: 'The History tab on an asset and on a part: who changed which field, from what to what, and why. Owners always can.',
    /** F8.1 */
    historyAccessTeams: 'And everyone on these teams:',
    role: {
      OWNER: 'Owners',
      ADMIN: 'Administrators',
      SUPERVISOR: 'Supervisors',
      MEMBER: 'Members',
    },
    operatingTitle: 'Operating windows and automation',
    operatingBody:
      'These values drive the attendance, safety, conduct, corrective-action, and AI services directly.',
    /** Was a tuple array `[key, label, min, max]` — a label inside a
     * module-level tuple is as invisible to the rule as one in an object. */
    field: {
      attendanceRolloverDays: 'Attendance history window (days)',
      safetyRolloverDays: 'Safety history window (days)',
      conductRolloverDays: 'Conduct history window (days)',
      autoCloseVerifiedCarsDays: 'Auto-close verified CARs after (days)',
      aiBudgetUsd: 'Monthly AI budget (USD)',
    },
    /** F35: one-sentence description shown under each operating field. */
    fieldHelp: {
      attendanceRolloverDays: 'Sets how many days of attendance history stay searchable before rolling off.',
      safetyRolloverDays: 'Sets how many days of safety history stay searchable before rolling off.',
      conductRolloverDays: 'Sets how many days of conduct history stay searchable before rolling off.',
      autoCloseVerifiedCarsDays: 'Closes a verified corrective action automatically after this many days. Leave blank to disable.',
      aiBudgetUsd: 'Caps what the AI features may spend per month. Leave blank for no cap.',
    },
    /** F23: where the retired "Monthly hours worked" setting went. */
    hoursMoved: 'Standard monthly hours for safety rates are set per facility, from a chosen month, beside the hours worked.',
    hoursMovedLink: 'Open Safety → Hours worked',
    operatingHint:
      'Leave optional values blank to disable the limit or automation. Supplier CARs are never auto-closed because their electronic-signature lifecycle must finish explicitly.',
    operatingSave: 'Save operating policies',
    operatingSaving: 'Saving…',
    operatingFailed: 'Could not update operating policies.',
  },

  numbering: {
    /**
     * F35: each record type by its PREFIX — its name and what gets numbered
     * with it (the server's `label`/`description` are English fallbacks).
     */
    types: {
      QC: { name: 'Quality checks', description: 'Numbers routine quality checks done on the floor.' },
      EQ: { name: 'Equipment checks', description: 'Numbers checks performed on a specific machine.' },
      SA: { name: 'Safety checks', description: 'Numbers safety rounds and safety inspections.' },
      SO: { name: 'Sign-off checks', description: 'Numbers formal sign-offs recorded as checks.' },
      CL: { name: 'Checklists', description: 'Numbers supervisor checklists and custom check types.' },
      CAR: { name: 'Corrective actions', description: 'Numbers corrective action requests.' },
      INC: { name: 'Incidents', description: 'Numbers safety incidents, near misses and damage reports.' },
      AUD: { name: 'Audits', description: 'Numbers internal and external audits.' },
      WO: { name: 'Work orders', description: 'Numbers maintenance work orders, planned or reactive.' },
      WR: { name: 'Work requests', description: 'Numbers problems reported from the floor or the public portal.' },
      DOC: { name: 'Controlled documents', description: 'Numbers policies, procedures and other controlled documents.' },
      RR: { name: 'Re-order requests', description: 'Numbers requests to buy more of a part.' },
      NCR: { name: 'Nonconformance reports', description: 'Numbers nonconforming product or process reports.' },
      CR: { name: 'Change requests', description: 'Numbers formal change-control requests.' },
      DEV: { name: 'Deviations', description: 'Numbers documented departures from an approved process.' },
      OOS: { name: 'Out-of-specification investigations', description: 'Numbers investigations of results outside specification.' },
      LOT: { name: 'Lots / batches', description: 'Numbers production lots and batches.' },
      HR: { name: 'HR records', description: 'Numbers confidential employee records.' },
      II: { name: 'Incoming inspections', description: 'Numbers inspections of goods received from suppliers.' },
      PM: { name: 'Maintenance plans', description: 'Numbers recurring preventive-maintenance plans.' },
      PL: { name: 'Check plans', description: 'Numbers the one-time and recurring plans that schedule checks.' },
      FRM: { name: 'Form templates', description: 'Numbers the templates forms and checklists are filled in from.' },
    },
    title: 'Numbering',
    /** `{before}` and `{after}` are sample entity numbers, built from a prefix
     * that is never translated. */
    description: 'Override the prefix and zero-padding for any entity number. Example: {before} → {after}.',
    warning:
      'Changing a prefix affects only numbers minted from now on — existing numbers are never rewritten, so records keep the handle they were issued under.',
    emptyTitle: 'No numbering prefixes registered',
    tableLabel: 'Numbering schemes',
    recordType: 'Record type',
    prefixOverride: 'Prefix override',
    padding: 'Padding',
    preview: 'Preview',
    prefixInputLabel: '{prefix} prefix override',
    paddingInputLabel: '{prefix} number padding',
    save: 'Save',
    saving: 'Saving…',
    saveFailed: 'Could not save this numbering override.',
  },

  features: {
    title: 'Features',
    description:
      'Company-wide capability switches with enforcement in navigation, APIs, and shared clients.',
    /** Was the `FEATURES` module constant, keyed on the settings field. */
    label: {
      chatEnabled: 'Chat and comments',
      uploadsEnabled: 'File uploads',
      exportsEnabled: 'Exports',
      aiEnabled: 'AI analytics',
      advancedDashboardEnabled: 'Advanced dashboard',
      anonymousIncidentsEnabled: 'Anonymous incident intake',
      allowNegativeStock: 'Allow negative stock',
      workRequestPortalEnabled: 'Public work-request portal',
    },
    consequence: {
      chatEnabled:
        'Disabling closes chat/comment APIs and removes chat navigation without deleting history.',
      uploadsEnabled:
        'Disabling prevents new uploads everywhere; existing governed files remain readable.',
      exportsEnabled:
        'Disabling prevents new export jobs; existing completed exports remain available until expiry.',
      aiEnabled: 'Disabling removes AI navigation and closes AI APIs and background work.',
      advancedDashboardEnabled:
        'Disabling uses the core operational dashboard without advanced analysis panels.',
      anonymousIncidentsEnabled:
        'Allows configured public/anonymous safety intake while keeping tenant routing controlled.',
      allowNegativeStock:
        'When off, inventory issues that would take on-hand stock below zero are rejected.',
      workRequestPortalEnabled:
        'When on, anyone with a facility’s request link or QR code can report a problem without signing in; requests land in Maintenance → Requests for triage. Off by default — every link answers “not found” until you turn it on.',
    },
    flagsTitle: 'Custom automation flags',
    /**
     * `<0>` wraps `companyFlags.<name>` in a `<code>` chip — a literal the
     * reader is meant to type, so it is inside the message rather than
     * concatenated around it, and only the placeholder word is translated.
     *
     * The angle brackets are REAL characters, not `&lt;`/`&gt;`: JSX decodes
     * entities at compile time, a catalogue string does not, and the first
     * version of this rendered `companyFlags.&lt;name&gt;` on screen. The slot
     * matcher only recognises `<digit>`, so `<name>` passes through untouched.
     */
    flagsBody:
      'Named flags are injected into every workflow trigger as <0>companyFlags.<name></0> and into webhook action payloads, so integrations and workflow conditions can branch on them.',
    noFlags: 'No custom flags yet.',
    toggleFlag: 'Toggle {name}',
    removeFlag: 'Remove',
    newFlagLabel: 'example: pilot.new_flow',
    addFlag: 'Add flag',
  },

  auditLog: {
    title: 'Audit log',
    description: 'The immutable company mutation trail: who changed which record, when, and why.',
    entityTypeFilter: 'Entity type',
    /** F3: one record's trail. */
    entityIdFilter: 'Record id',
    actorFilter: 'Actor (user id)',
    /** The change column's disclosure (2026-09-18). */
    showChange: 'Show change',
    hideChange: 'Hide change',
    from: 'From',
    to: 'To',
    tableLabel: 'Audit log entries',
    when: 'When',
    actor: 'Actor',
    action: 'Action',
    record: 'Record',
    change: 'Change',
    /** The actor of a change nobody made by hand — a cron, a webhook, the
     * platform itself. */
    system: 'System',
    /** FF-AUDIT-20260921-210633-027 / -012: automation rows name the rule; the record id links to the record. */
    automation: 'Automation · {rule}',
    triggeredBy: 'triggered by {actor}',
    correction: 'Correction',
    reasonPrefix: 'Reason: {reason}',
    empty: 'No matching audit entries.',
    loadMore: 'Load older entries',
    loading: 'Loading…',
  },

  systemHealth: {
    title: 'System health',
    description:
      'Scheduled jobs, delivery queues, automations, exports, and unresolved assignment work for this company.',
    /**
     * The three banner states. Was the `BANNER` module constant, where the
     * headline sat beside a Tailwind class — the words move out and the
     * colours stay, the same split every `*_TONE` map in this migration has
     * had.
     */
    banner: {
      HEALTHY: 'All monitored systems healthy',
      NOTICE: 'Nothing is broken — a few things to know',
      DEGRADED: 'Attention required',
    },
    checkedAt: 'Checked {when} · refreshes every minute',
    webhookFailures: 'Webhook failures',
    exportsQueued: 'Exports queued/running',
    automationFailures: 'Automation failures (24h)',
    unresolvedAssignments: 'Unresolved assignments',
    dependencies: 'Dependencies',
    /** The dependency probe's two-value status — a raw enum on screen before
     * this. */
    dependencyStatus: {
      HEALTHY: 'Healthy',
      FAILED: 'Failed',
    },
    dependencyTiming: '{ms} ms',
    dependencyTimingError: '{ms} ms · {error}',
    cronTitle: 'Scheduled jobs',
    cronBody:
      'A job is stale after two missed expected intervals plus a short scheduler allowance. Jobs that have never run show muted — expected while scheduled jobs are not enabled for this environment.',
    cronTableLabel: 'Cron jobs',
    cronJob: 'Job',
    cronStatusColumn: 'Status',
    cronLastStart: 'Last start',
    cronExpected: 'Expected',
    cronProcessed: 'Processed',
    /** Was `job.status.replace('_', ' ')` — a prettifier (rule 9) that turned
     * `NEVER_RUN` into `NEVER RUN`, a plausible English phrase that could
     * never have been anything else. */
    cronStatus: {
      HEALTHY: 'Healthy',
      STALE: 'Stale',
      FAILED: 'Failed',
      NEVER_RUN: 'Never run',
    },
    cronNever: 'Never',
    cronEvery: '{minutes} min',
  },

  // ── chunk 5f-2: users and access ───────────────────────────────────────

  /**
   * `Role`, `UserStatus` and `FacilityScope` are NOT here. `workforce` owns the
   * first two (they label a person) and `facilities` the third; this slice
   * reads their catalogues. The twelve facility-posture module names likewise
   * come from `facilitiesCopy.postureModule` — this file spelled seven of them
   * differently from Settings → Facilities before 5f-2, which is the whole
   * argument for one owner per enum.
   */
  users: {
    title: 'Users',
    description:
      'Invite teammates, manage roles and status, permission overrides, and active sessions.',
    invite: '+ Invite user',
    emptyTitle: 'No users yet',
    emptyBody: 'Invite your first teammate to get started.',
    tableLabel: 'Users',
    /** The toolbar (2026-09-18). */
    searchPlaceholder: 'Search name or e-mail…',
    includeArchived: 'Include archived',
    name: 'Name',
    email: 'Email',
    role: 'Role',
    status: 'Status',
    /** The 2FA column. The acronym is not translated. */
    twoFactor: '2FA',
    twoFactorOn: 'Enabled',
  },

  invite: {
    title: 'Invite a user',
    description:
      "They'll receive an email with a link to accept and set a password. The link expires after 7 days.",
    email: 'Email',
    role: 'Role',
    facilities: 'Facilities (defaults to the primary facility)',
    primaryNote: 'The first selected facility becomes their primary.',
    failed: 'Could not send the invite.',
    cancel: 'Cancel',
    send: 'Send invite',
    sending: 'Sending…',
  },

  userDetail: {
    ownerRowNote: 'Owner row — role and status are immutable here.',
    firstName: 'First name',
    lastName: 'Last name',
    role: 'Role',
    status: 'Status',
    cannotDisableSelf: 'You cannot disable your own account.',
    /** Owner decision 2026-09-16 (round 2): disabling a login is an explained action, not a dropdown. */
    login: {
      label: 'Login',
      disable: 'Disable login…',
      cancelInvite: 'Cancel invitation…',
      enable: 'Re-enable login…',
      disableTitle: 'Disable {name}’s login?',
      cancelInviteTitle: 'Cancel {name}’s invitation?',
      enableTitle: 'Re-enable {name}’s login?',
      disableWhat: 'They can no longer sign in and any open session ends now. Their e-mail stays reserved on this account, and everything they did stays on record. Their worker record, if any, is untouched — archive it from the Workers page if they have left.',
      cancelInviteWhat: 'The invitation link stops working and the account is disabled. Their e-mail stays reserved on this account; you can re-enable and re-invite later.',
      enableWhat: 'They can sign in again with their existing password.',
      enableNote: 'If they never set a password (the account was invited and then disabled), send them a password reset afterwards.',
      keep: 'Keep as is',
      disableConfirm: 'Disable login',
      cancelInviteConfirm: 'Cancel invitation',
      enableConfirm: 'Re-enable login',
      disabledNote: 'Login disabled — open sessions ended.',
      invitationCancelledNote: 'Invitation cancelled — the link no longer works.',
      enabledNote: 'Login re-enabled.',
    },
    saveFailed: 'Could not save this user.',
    saved: 'Saved.',
    save: 'Save',
    saving: 'Saving…',
    resend: 'Resend invite',
    resending: 'Sending…',
    resent: 'Invite re-sent.',
    resendFailed: 'Could not re-send the invite.',
    permissionOverrides: 'Permission overrides',
    ownerHasEverything: "The owner always has every permission — overrides don't apply.",
    cannotEditOwnPermissions: 'You cannot edit your own permission overrides.',
    sessions: 'Sessions',
    /** FF-AUDIT-20260914-210459-001: the worker record link, or its absence, on the account itself. */
    workerRecord: 'Worker record',
    /** FF-AUDIT-20260921-210633-011: four subjects, four tabs. */
    tabs: {
      region: 'Sections of this person’s record',
      profile: 'Profile',
      access: 'Access',
      permissions: 'Permissions',
      ownership: 'Ownership',
      sessions: 'Sessions',
    },
    workerRecordLinked: 'This account has a worker record: HR profile, training and shift schedule.',
    openWorkerRecord: 'Open worker record',
    workerRecordMissing: 'This account has no worker record. It can still be given work, but it is invisible to HR, training and the shift schedule until one exists.',
    createWorkerRecord: 'Create worker record',
    creatingWorkerRecord: 'Creating…',
    createWorkerRecordFailed: 'Could not create the worker record.',
  },

  ownership: {
    title: 'Ownership',
    /** `{note}` is the server's own confirmation sentence — still English,
     * Phase 8. Kept as a placeholder so the leading word is translatable now. */
    done: 'Done. {note}',
    /** Was the `OWNERSHIP_ACTIONS` module constant. */
    action: {
      PROMOTE: 'Make co-owner',
      TRANSFER: 'Transfer ownership',
      DEMOTE: 'Remove owner role',
    },
    confirm: {
      PROMOTE:
        'Give this person full owner access alongside you. Owners can do everything, including changing ownership.',
      TRANSFER:
        'Make this person the owner and step yourself down to admin. You will lose owner-only access.',
      DEMOTE:
        'Step this owner down to the selected role. A company must always keep at least one active owner.',
    },
    newRole: 'New role',
    password: 'Confirm with your password',
    totp: 'Authenticator code',
    working: 'Working…',
    cancel: 'Cancel',
    failed: 'Could not change ownership.',
  },

  permissions: {
    loading: 'Loading permissions…',
    loadFailed: 'Could not load permissions.',
    /** A permission the user's ROLE already grants, so an override is a
     * deliberate departure rather than the only source. */
    roleDefault: '(role default)',
    /**
     * FF-AUDIT-20260921-210633-011 — what each permission actually lets a
     * person do.
     * 
     * The editor listed 155 raw codes: `users:archive`, `hr_records:
     * read_confidential`, `workforce:read_sensitive`. An HR admin asked to
     * "let Grace see HR records" had no way to map that onto a code, so the
     * safe move was to grant nothing or to grant too much.
     * 
     * Written out rather than derived. A runtime prettifier of the code is
     * the defect class this repo keeps paying for: it produces English word
     * order in every language and wording nobody reviewed. A code with no
     * entry here renders as the CODE — never as a guess.
     * 
     * The code stays on the row beside the sentence: it is what an auditor
     * matches against the API.
     */
    name: {
      'ai:configure': 'Set up the AI assistant for the company',
      'ai:use': 'Use the AI assistant',

      'assignment:assign': 'Assign work to other people',
      'assignment:claim': 'Claim work from the queue',
      'assignment:manage': 'Set up the rules that decide who gets work',
      'assignment:override': 'Override an assignment the rules made',
      'assignment:read': 'See who work is assigned to',

      'attachments:create': 'Upload files and photos',
      'attachments:read': 'Open files and photos',

      'attendance:approve': 'Approve time off and attendance entries',
      'attendance:read': 'See their own attendance',
      'attendance:read_all': 'See everyone’s attendance',
      'attendance:report': 'Record attendance and time off',

      'audit:read': 'Read the audit log',

      'audits:archive': 'Archive audits',
      'audits:create': 'Start an audit',
      'audits:manage': 'Set up audit programmes and templates',
      'audits:read': 'See audits',

      'cars:archive': 'Archive corrective actions',
      'cars:create': 'Raise a corrective action',
      'cars:delete': 'Delete a corrective action',
      'cars:manage': 'Set up how corrective actions work',
      'cars:read': 'See corrective actions',
      'cars:reopen': 'Reopen a closed corrective action',
      'cars:self_verify': 'Verify a corrective action they carried out themselves',
      'cars:update': 'Edit corrective actions',
      'cars:verify': 'Verify that a corrective action worked',

      'change:approve': 'Approve change requests',
      'change:archive': 'Archive change requests',
      'change:create': 'Raise a change request',
      'change:delete': 'Delete a change request',
      'change:manage': 'Set up how change control works',
      'change:read': 'See change requests',
      'change:update': 'Edit change requests',

      'change_control:self_approve': 'Approve a change request they raised themselves',

      'chat:admin': 'Administer chat, including other people’s channels',
      'chat:manage_channels': 'Create and manage chat channels',
      'chat:use': 'Send and read chat messages',

      'checks:archive': 'Archive checks',
      'checks:complete': 'Carry out and complete a check',
      'checks:create': 'Create a check',
      'checks:delete': 'Delete a check',
      'checks:manage': 'Set up check types and how checks work',
      'checks:plan': 'Build and edit check schedules',
      'checks:read': 'See checks',
      'checks:reopen': 'Reopen a completed check',
      'checks:update': 'Edit checks',

      'comments:moderate': 'Remove or edit other people’s comments',
      'comments:use': 'Write comments',

      'deviations:approve': 'Approve deviations',
      'deviations:archive': 'Archive deviations',
      'deviations:create': 'Raise a deviation',
      'deviations:delete': 'Delete a deviation',
      'deviations:manage': 'Set up how deviations work',
      'deviations:read': 'See deviations',

      'deviations_oos:self_approve': 'Approve a deviation they raised themselves',

      'documents:create': 'Add documents',
      'documents:distribute': 'Issue a document to people for reading',
      'documents:manage': 'Set up the document library and its approvals',
      'documents:read': 'Open documents',

      'downtime:manage': 'Set up downtime reasons and costs',
      'downtime:read': 'See downtime',
      'downtime:report': 'Record that a machine is down or back up',

      'equipment:archive': 'Archive equipment',
      'equipment:create': 'Add equipment',
      'equipment:ingest_metrics': 'Send machine readings in from a gateway',
      'equipment:read': 'See equipment',
      'equipment:update': 'Edit equipment',

      'esign:read': 'See electronic signature records',

      'events:read': 'Read the raw event stream',

      'facilities:manage': 'Add and edit facilities',
      'facilities:move_records': 'Move a record from one facility to another',
      'facilities:read': 'See the list of facilities',
      'facilities:share_content': 'Share a template or document across facilities',

      'forms:manage': 'Build and publish forms',
      'forms:read': 'See forms',
      'forms:submit': 'Fill in and submit a form',

      'help:raise': 'Ask for help from the floor',
      'help:resolve': 'Answer and close help requests',

      'hr_records:archive': 'Archive HR records',
      'hr_records:correct': 'File a correction to an issued HR record',
      'hr_records:create': 'Create an HR record',
      'hr_records:delete': 'Delete an HR record',
      'hr_records:manage': 'Act as an HR designate — see and manage every HR record',
      'hr_records:read': 'See HR records for the people they are responsible for',
      'hr_records:read_own': 'See their own HR records',

      'incidents:approve': 'Sign off a safety investigation',
      'incidents:investigate': 'Investigate a safety incident',
      'incidents:manage': 'Set up how safety incidents work',
      'incidents:read': 'See safety incidents',
      'incidents:report': 'Report a safety incident or near miss',

      'integrations:manage': 'Set up integrations and API clients',
      'integrations:read': 'See integrations',

      'locations:manage': 'Add and edit locations within a facility',

      'lots:archive': 'Archive lots',
      'lots:delete': 'Delete a lot',
      'lots:manage': 'Set up lot and genealogy tracking',
      'lots:read': 'See lots and their genealogy',
      'lots:transact': 'Record lot movements and consumption',

      'maintenance:create': 'Create a work order (who it can go to follows the assignment rules)',
      'work:transfer': 'Transfer a person’s work to someone else, and send it back',
      'maintenance:execute': 'Carry out a work order',
      'maintenance:manage': 'Set up how maintenance works',
      'maintenance:plan': 'Build and edit maintenance plans',
      'maintenance:read': 'See work orders',
      'maintenance:request': 'Ask for maintenance work',

      'ncr:archive': 'Archive nonconformances',
      'ncr:create': 'Raise a nonconformance',
      'ncr:delete': 'Delete a nonconformance',
      'ncr:disposition': 'Decide what happens to nonconforming material',
      'ncr:manage': 'Set up how nonconformances work',
      'ncr:read': 'See nonconformances',
      'ncr:update': 'Edit nonconformances',

      'notes:archive': 'Archive notes',
      'notes:create': 'Write notes',
      'notes:read': 'Read notes',
      'notes:update': 'Edit notes',

      'notifications:read': 'Receive notifications',

      'oos:archive': 'Archive out-of-specification results',
      'oos:conclude': 'Conclude an out-of-specification investigation',
      'oos:delete': 'Delete an out-of-specification result',
      'oos:investigate': 'Investigate an out-of-specification result',
      'oos:read': 'See out-of-specification results',

      'parts:adjust': 'Adjust stock levels',
      'parts:manage': 'Add and edit parts',
      'parts:manage_reorders': 'Approve and receive re-order requests',
      'parts:read': 'See parts and stock',
      'parts:request_reorder': 'Ask for a part to be re-ordered',
      'parts:send_supplier_email': 'E-mail a re-order to a supplier',

      'permissions:update': 'Change what other people are allowed to do',

      'reports:read': 'Open reports and dashboards',
      'reports:schedule': 'Schedule a report to be sent regularly',
      'exports:create': 'Export data from registers and reports',

      // Record visibility (owner decision 2026-10-01): without these a person
      // sees only the records of the area that concern them.
      'checks:read_all': 'See every check and check plan, not only their own',
      'cars:read_all': 'See every corrective action, not only their own',
      'audits:read_all': 'See every audit and finding, not only their own',
      'maintenance:read_all': 'See every work order and maintenance plan, not only their own',
      'assignment:read_all': 'See everyone’s assignment decisions, the current workload and the decision log',
      // Round 2: the middle scope — their own plus everyone ranking below them.
      'checks:read_below': 'See their own checks and those of people below them',
      'cars:read_below': 'See their own corrective actions and those of people below them',
      'audits:read_below': 'See their own audits and those of people below them',
      'maintenance:read_below': 'See their own work orders and plans and those of people below them',
      'assignment:read_below': 'See the workload and decisions of themselves and people below them',

      'risk:archive': 'Archive risk entries',
      'risk:delete': 'Delete a risk entry',
      'risk:manage': 'Add and edit the risk register and FMEAs',
      'risk:read': 'See the risk register and FMEAs',

      'roster:manage': 'Build and publish the shift roster',
      'roster:read_all': 'See everyone’s shifts',

      'search:manage_identifiers': 'Manage the identifiers records are found by',

      'sessions:read_all': 'See everyone’s sign-in sessions',
      'sessions:revoke_all': 'Sign other people out of their devices',

      'settings:read': 'See company settings',
      'settings:update': 'Change company settings',

      'supplier_quality:read': 'See vendor scorecards and incoming inspections',
      'supplier_quality:inspect': 'Carry out an incoming inspection',
      'supplier_quality:manage': 'Set up supplier quality — scorecards, inspection plans, approvals',

      'training:manage': 'Set up training requirements and courses',
      'training:read': 'See training records',
      'training:record': 'Record that someone completed training',
      'training:schedule': 'Schedule training sessions',

      'users:archive': 'Deactivate a user account',
      'users:invite': 'Invite someone to the company',
      'users:read': 'See the list of people',
      'users:update': 'Edit a person’s name, role and login',

      'vendors:manage': 'Add and edit vendors',
      'vendors:read': 'See vendors',

      'workflows:manage': 'Build and edit automation playbooks',

      'workforce:manage': 'Add and edit worker records',
      'workforce:read': 'See worker records',
      'workforce:read_sensitive': 'See confidential worker details, including pay',
    },
    /**
     * FF-AUDIT-20260921-210633-011: finding ONE permission among 155.
     *
     * The list was 155 selects under raw registry keys, in one 8,748 px
     * scroll. Two controls do most of the work: type what you are looking
     * for, or show only the rows that differ from the role — which is the
     * question anyone auditing a person's access is actually asking.
     */
    searchLabel: 'Find a permission',
    searchPlaceholder: 'Search by module or permission…',
    onlyChanged: 'Only those changed from the role default',
    changedCount: '{count} changed from the role default',
    noMatches: 'No permission matches that.',
    clearSearch: 'Show all',
    overrideLabel: 'Override for {permission}',
    /** `PermissionEffect` plus the "no override" state. */
    effect: {
      INHERIT: 'Inherit',
      GRANT: 'Grant',
      REVOKE: 'Revoke',
    },
    /** FF-AUDIT-061 — the per-person separation-of-duties exemptions. */
    help: {
      carsSelfVerify: 'May take their own CARs through every step: verify and close work they assigned to themselves or submitted.',
      changeControlSelfApprove: 'May approve change requests they raised themselves.',
      /** Owner decision 2026-09-16 (round 2). */
      deviationsOosSelfApprove: 'May approve deviations and conclude OOS investigations they raised themselves.',
    },
    saved: 'Saved.',
    save: 'Save permission overrides',
    saving: 'Saving…',
    saveFailed: 'Could not save permission overrides.',
  },

  sessions: {
    loading: 'Loading sessions…',
    loadFailed: 'Could not load sessions.',
    empty: 'No active sessions.',
    /** A browser user-agent string the server did not record. */
    unknownDevice: 'Unknown device',
    /** Was `{ip} · ` concatenated onto `Last active {date}` — two fragments
     * assembled in the JSX, so French could not reorder them (rule 5). */
    lastActive: 'Last active {when}',
    lastActiveFrom: '{ip} · Last active {when}',
    revoke: 'Revoke',
    revokeFailed: 'Could not revoke that session.',
    revokeAll: 'Revoke all sessions',
    revokingAll: 'Revoking…',
    revokeAllFailed: 'Could not revoke sessions.',
  },

  access: {
    /**
     * F35: every area by its `PAGE_ACCESS_CATALOG` key — its name (the contract's
     * `label` is English) and what granting it opens.
     */
    areas: {
      dashboard: { name: 'Dashboard', description: 'Opens the home dashboard with the plant’s key numbers and today’s work.' },
      checks: { name: 'Checks', description: 'Opens checks: running them, the check register and recurring check plans.' },
      cars: { name: 'Corrective actions', description: 'Opens corrective actions (CARs): raising, working and verifying them.' },
      audits: { name: 'Audits & inspections', description: 'Opens audits and inspections: scheduling, running them and their findings.' },
      assignment: { name: 'Assignment', description: 'Opens the assignment consoles that route work to people.' },
      equipment: { name: 'Equipment & downtime', description: 'Opens the equipment register, machine pages, floorplans and downtime.' },
      maintenance: { name: 'Maintenance', description: 'Opens work orders and maintenance plans.' },
      parts: { name: 'Parts & vendors', description: 'Opens the parts catalog, stock, re-order requests and vendors.' },
      safety: { name: 'Safety', description: 'Opens safety: incidents, help requests and the safety dashboard.' },
      training: { name: 'Training', description: 'Opens training: courses, sessions, certifications and the matrix.' },
      workforce: { name: 'Workforce', description: 'Opens people pages: workers, attendance, the roster, locations and the organization.' },
      documents: { name: 'Documents', description: 'Opens the document library and document distributions.' },
      forms: { name: 'Forms', description: 'Opens the form library and form submissions.' },
      chat: { name: 'Chat & inbox', description: 'Opens chat channels, direct messages and the inbox.' },
      reports: { name: 'Reports', description: 'Opens reports, dashboards and data exports.' },
      ai: { name: 'AI analytics', description: 'Opens the AI hub and its analyses.' },
      integrations: { name: 'Integrations', description: 'Opens webhooks, API keys, connectors and integration logs.' },
      automation: { name: 'Automation', description: 'Opens automation playbooks and their settings.' },
      notes: { name: 'Notes', description: 'Opens personal and shared notes.' },
      settings: { name: 'Settings', description: 'Opens Settings; each settings page still checks its own permission.' },
      ncr: { name: 'Nonconformance', description: 'Opens nonconformance reports and defect codes.' },
      change_control: { name: 'Change control', description: 'Opens change requests and the change-approval matrix.' },
      supplier_quality: { name: 'Supplier quality', description: 'Opens supplier quality: approved suppliers, incoming inspection and SCARs.' },
      risk_fmea: { name: 'Risk & FMEA', description: 'Opens the risk register and FMEA worksheets.' },
      deviations_oos: { name: 'Deviations & OOS', description: 'Opens deviations and out-of-specification investigations.' },
      lot_genealogy: { name: 'Lot genealogy', description: 'Opens lots, holds and lot traceability.' },
      regulated_esign: { name: 'Electronic signatures', description: 'Opens the electronic-signature audit trail.' },
      hr_records: { name: 'HR records', description: 'Opens HR records; who sees which record is still set in HR settings.' },
    },
    title: 'Page access',
    description:
      'What each role can open. A tick means the role can reach the area: its permissions allow it and this matrix grants it. Owners always retain all pages.',
    /** FF-AUDIT-080: a box the role's permissions can never satisfy. */
    unreachableTitle: 'The {role} role has no {permission} permission, so it cannot open this area whatever this matrix says. Grant that permission per person under Settings → Users.',
    unreachableLegend: 'Greyed boxes name the permission the role lacks — that area stays closed to the role until the permission is granted per person under Settings → Users.',
    tableLabel: 'Role access',
    area: 'Area',
    checkboxLabel: '{area} for {role}',
    save: 'Save access matrix',
    saving: 'Saving…',
    saved: 'Saved. Other sessions update through the live settings feed.',
  },

  /** F20 (OD-10): Settings → Assignment rules. */
  assignmentRules: {
    title: 'Assignment rules',
    description: 'Who may give each kind of work to whom. The owner may always assign anyone.',
    escalationNote:
      'Escalation always works: anyone can hand work up to their supervisor, even someone they cannot assign work to. The policy picks who receives it, never the person escalating.',
    workTypeLabel: 'Work type',
    workTypes: {
      CHECK: 'Checks',
      CHECK_PLAN: 'Check plans',
      WORK_ORDER: 'Work orders',
      MAINTENANCE_PLAN: 'Maintenance plans',
      AUDIT: 'Audits',
      CAR: 'Corrective actions',
    },
    /** Column headings: who the work may be given to. */
    targets: {
      SELF: 'Themselves',
      MEMBER: 'Members',
      SUPERVISOR: 'Supervisors',
      ADMIN: 'Admins',
      OWNER: 'The owner',
      TEAM: 'A team',
      DISPATCH: 'Let dispatch choose',
    },
    tableLabel: 'Who may assign {workType} to whom',
    role: 'Assigned by',
    ownerAlways: 'The owner may always assign anyone.',
    checkboxLabel: '{role} may assign {workType} to {target}',
    changed: 'Changed from the default',
    save: 'Save rules',
    saving: 'Saving…',
    saved: 'Saved.',
    restore: 'Restore defaults for {workType}',
    exceptions: {
      heading: 'Per-person exceptions',
      body: 'An exception replaces one person’s role rule for one kind of work — for example, a lead hand who may assign work orders to other supervisors.',
      tableLabel: 'Per-person exceptions',
      person: 'Person',
      workType: 'Work type',
      mayAssignTo: 'May assign to',
      nobody: 'Nobody',
      setBy: 'Set by',
      add: '+ New exception',
      empty: 'No exceptions. Everyone follows their role’s rule.',
      edit: 'Edit',
      remove: 'Remove',
      rowMenu: 'Actions for {name}, {workType}',
      removeTitle: 'Remove {name}’s exception?',
      removeBody: 'They follow their role’s rule for {workType} again.',
      newTitle: 'New exception',
      editTitle: 'Edit exception',
      dialogBody: 'Tick who this person may give this kind of work to. It replaces their role’s rule for this work type only.',
      personLabel: 'Person',
      targetsLegend: 'May assign to',
      save: 'Save exception',
      saving: 'Saving…',
      cancel: 'Cancel',
    },
  },

  facilityAccess: {
    title: 'Facility access',
    assigned: 'Assigned facilities',
    primary: 'Primary facility (their default on login)',
    visibilityBody: 'Visibility per module — what this user sees beyond their own facility.',
    rolesBody:
      'Per-facility roles — a supervisor at one plant is not a supervisor at another. “{everywhere}” rows apply across all facilities (management).',
    /** The `facilityId: null` row: a role that is not tied to one site. */
    everywhere: 'Everywhere',
    facility: 'Facility',
    role: 'Role',
    removeRole: 'Remove',
    addRole: '+ Add facility role',
    save: 'Save facility access',
    saving: 'Saving…',
    saved: 'Saved.',
    saveFailed: 'Could not save facility access.',
  },
})

export type SettingsCopy = typeof settingsCopy.en
